const MAX_SAVE_ATTEMPTS = 5;
const RETRY_INTERVAL_FACTOR = 500; //ms

export const INVITATION_ASSESSMENTS = ['prapare', 'pearls'];
export const ASSESSMENT_URL_PARAMETERS = ['agency_id', 'invitation_id', 'invitation_token'];
export const ASSESSMENT_CHECK_DATA_FIELDS = ['ext_id_check', 'phone_check', 'status']; // status needed?
export const STATUS_STARTED = 'started';
export const STATUS_FINISHED = 'finished';
export const STATUS_UNSTARTED = 'unstarted';
export const PREFER_NOT_TO_ANSWER = 'I prefer not to answer this question'; // TODO this is "Skip" in some assessments

export const ASSESSMENT_SECTIONS = ['intro', 'question', 'screen', 'review', 'results'];

export const timestamp = () => new Date().getTime();

export const parsePersonName = (user) => (user?.id ? [user.first_name || '', user.last_name || ''].join(' ').trim() : null);

export const isEmptyObj = (obj) => !obj || Object.keys(obj).length === 0;
export const getInvitationData = (query) => (query ? { id: query.invitation_id, token: query.invitation_token } : null);

export const apiCall = async (fn) => {
  let attempts = 0;
  let err = null;
  while (attempts < MAX_SAVE_ATTEMPTS) {
    try {
      return await scheduleCall(fn, attempts++);
    } catch (ex) {
      err = ex;
    }
  }
  throw err;
};

function scheduleCall(fn, attempts) {
  return new Promise((resolve, reject) => {
    const delay = Math.pow(2, attempts - 1) * RETRY_INTERVAL_FACTOR;
    setTimeout(
      async () => {
        try {
          resolve(await fn());
        } catch (ex) {
          reject(ex);
        }
      },
      attempts === 0 ? 0 : delay
    );
  });
}

export const saveAll = async (assessment, answers, screenData) => {
  const data = { assessment, answers, screenData };
  return { res: { ok: true }, data };
};

export const getSequence = (assessment, metadata) => {
  const questions = assessment.questions.map((q) => ({ type: 'question', value: q.id }));
  const before = getPages('before', metadata).map((p) => ({ type: 'pre', value: p }));
  const after = getPages('after', metadata).map((p) => ({ type: 'post', value: p }));
  const sequence = [...before, ...questions, ...after];
  const opts = metadata.options || {};
  if (!opts.skip_intro) {
    sequence.unshift({ type: 'intro', value: 'intro' });
  }
  if (!opts.skip_review) {
    sequence.push({ type: 'review', value: 'review' });
  }
  return sequence;
};

/*
  Return the question meta data based on a questionId
*/
export const getQuestionMeta = (questionId, metadata) => {
  let found = metadata?.profile_questions?.filter((q) => q.id === questionId);
  if (found?.length) {
    return found[0];
  } else {
    return false;
  }
};

/*
  Centralized logic for spacing centered content mainly for the 'basic' assessment layout
*/
export const centeredContentCols = () => {
  return {
    xs: {
      span: 12
    },
    sm: {
      span: 10,
      offset: 1
    },
    md: {
      span: 8,
      offset: 2
    },
    lg: {
      span: 6,
      offset: 3
    }
  };
};

export const typeToSection = (type) => {
  if (['pre', 'post'].includes(type)) {
    return 'screen';
  } else {
    return ASSESSMENT_SECTIONS.includes(type) ? type : 'default';
  }
};

export const colsByLayout = (layout) => {
  let cols = {
    default: { container: centeredContentCols(), left: { xs: 12 }, right: { xs: 12 }, buttons: { xs: 12 } }
  };

  ASSESSMENT_SECTIONS.forEach((section) => {
    switch (layout) {
      case 'detailed_progress':
        cols[section] = {
          container: section == 'intro' ? centeredContentCols() : { xs: 12 },
          left: {
            md: 4
          },
          right: {
            md: 8
          },
          buttons: {
            md: {
              span: 8,
              offset: 4
            }
          }
        };
        break;
      case 'basic':
        if (section === 'results') {
          cols[section] = {
            container: { xs: 12 },
            leftClass: 'rounded-corner-panel',
            rightClass: '',
            left: {
              md: 4
            },
            right: {
              md: 8
            },
            buttons: {
              md: {
                span: 8,
                offset: 4
              }
            }
          };
        } else {
          cols[section] = { container: centeredContentCols(), left: { xs: 12 }, right: { xs: 12 }, buttons: { xs: 12 } };
        }
        break;
    }
  });

  return cols;
};

/**
 * Get the index of the next question to ask.
 *
 * @param questions - map of id => question for all questions in the assessment
 * @param answers - map of the current answers so far
 * @param step - number of the current step in the assessment flow
 * @param currentQuestion - the question that has just been answered
 * @param currentAnswer - the answer provided for the `currentQuestion`
 * @param reviewing - boolean for whether the person completed the assessment and are now going back to questions
 * @returns {integer} - the index of the next question
 */
export const getNextStep = (
  questions,
  answers,
  step,
  sequence,
  currentQuestion,
  currentAnswer,
  reviewing,
  prompts = {},
  finishedPrompts = [],
  dependentQuestionList
) => {
  const modalPrompt = getRequiredPrompt(currentQuestion, sequence[step], prompts, finishedPrompts);
  if (modalPrompt) return { modalPrompt };
  const skipToId = findQuestionToSkipTo(currentQuestion, currentAnswer, reviewing);
  for (let i = step + 1; i < sequence.length; i++) {
    const seq = sequence[i];
    // The complicated conditions can be summarized as 'return the step in the sequence when':
    // 1. skipToId matches a sequence type (i.e. the steps for 'before', 'review', etc...) return to that step
    // 2. Not in 'reviewing' mode (i.e. currently navigating using the normal flow of the assessment) AND
    //   a. skipToId is not set and sequence type is not a question (i.e. 'review', 'after', etc...)  OR
    //   b. sequence type is a question and that question should be shown (based on skip and previous answer logic)
    // 3. In 'reviewing' mode (i.e. have clicked 'edit' on a specific question from the review step) AND
    //   a. sequence type is a question that should be shown due to dependency or skip logic OR
    //   b. it's the 'review' step (i.e. return to the review step with updated answers)
    if (
      seq.type === skipToId ||
      (!reviewing &&
        ((!skipToId && seq.type !== 'question') || (seq.type === 'question' && showQuestion(questions[seq.value], answers, skipToId)))) ||
      (reviewing &&
        ((seq.type === 'question' && showDependentQuestion(questions[seq.value], answers, skipToId, dependentQuestionList)) || seq.type === 'review'))
    ) {
      return { nextStep: i };
    }
  }
  return { nextStep: null };
};

export const getRequiredPrompt = (currentQuestion, currentSequence, prompts, finishedPrompts) => {
  const key = currentSequence?.type === 'review' ? 'review' : typeof currentQuestion === 'string' ? currentQuestion : currentQuestion.id;
  if (!prompts[key]) return null;
  const prompt = prompts[key].find((m) => !finishedPrompts.includes(m.id) && !m.finished);
  return prompt ? { prompt, key } : null;
};

const showDependentQuestion = (q, answers, skipToId, dependentQuestionList) => {
  const foundDependentQuestionIndex = dependentQuestionList.indexOf(q.id);
  // Some dependent questions could be skipped, so remove them from the queue regardless if they are the question to be shown
  if (foundDependentQuestionIndex >= 0) {
    dependentQuestionList.splice(foundDependentQuestionIndex, 1);
  }
  return foundDependentQuestionIndex >= 0 && showQuestion(q, answers, skipToId);
};

/**
 * Determine whether the given question should be asked next.
 *
 * @param q - the question to inspect as the possible next question to ask
 * @param answers - map of the current answers so far
 * @param skipToId - optional id of the next question we want to show
 * @returns {boolean} - whether question `q` should be skipped
 */
const showQuestion = (q, answers, skipToId) => {
  if (skipToId) {
    return q.id === skipToId;
  }
  if (q.ask_if_question_id && q.ask_if_question_value) {
    // only want to ask this question if the person has answered `q.ask_if_question_value` for question id `q.ask_if_question_id`
    const answer = getAnswer(q.ask_if_question_id, answers);
    if (answer) {
      return answersAreEqual(answer.value, q.ask_if_question_value) || answersIncludeValue(answer.value, q.ask_if_question_value);
    } else {
      // if required answer isn't found, then skip any question that is conditional the required answer
      return false;
    }
  }
  return true;
};

/**
 * Find an answer by id.
 */
const getAnswer = (question_id, answers) => Object.values(answers).find((val) => val.question_id === question_id);

/**
 * Compare two values for equality, allowing for case-insensitivity or parsing strings to numbers
 * as needed.
 */
export const answersAreEqual = (a1, a2) => {
  if (a1 === a2) return true;
  const t1 = typeof a1;
  const t2 = typeof a2;
  if (t1 === 'undefined' || t2 === 'undefined') return false;
  if (t1 === 'string' && t2 === 'string') {
    // ignore string case
    return a1.trim().toLowerCase() === a2.trim().toLowerCase();
  }
  if (Array.isArray(a1) && Array.isArray(a2)) {
    return a1.length === a2.length && a1.toSorted().every((item, index) => answersAreEqual(item, a2.toSorted()[index]));
  }
  // TODO other cases?
  return false;
};

/**
 * For multi-select questions, check for a specific value in the chosen answers
 * This method assumes that answerArray is either a string or an array of strings.
 * It will throw a JavaScript error if that is not true.
 */
const answersIncludeValue = (answerArray, value) => {
  if (!Array.isArray(answerArray)) {
    answerArray = [answerArray];
  }
  return typeof value === 'string' && answerArray.map((a) => a.trim().toLowerCase()).includes(value.trim().toLowerCase());
};

/**
 * Based on the most recent question's answer, look for a question to skip directly to.
 *
 * If the question that was just answered defines the skip_to_question_* fields, find the answer at
 * index `i` in the `skip_to_question_value` list, then skip to the question id at index `i` in the
 * `skip_to_question_id` list. Otherwise do not skip directly to a specific question.
 */
const findQuestionToSkipTo = (currentQuestion, currentAnswer, reviewing) => {
  if (currentQuestion.skip_to_question_id && currentQuestion.skip_to_question_value) {
    const ids = currentQuestion.skip_to_question_id;
    const vals = currentQuestion.skip_to_question_value;
    const index = vals.findIndex((val) => answersAreEqual(val, currentAnswer));
    if (index >= 0) {
      return ids[index];
    }
  }
  return null;
};

export const extractLocation = ({ metadata, screenData, questions, answers }) => {
  return findQuestionAnswer(questions, answers, isLocationQuestion) || findPageResult(metadata, screenData, isLocationPage);
};

// `entity` can be tags or properties.  currently all existing maps only use `tags`.
export const extractMappedValues = (optionMap, opt, entity = 'tags') => {
  if (optionMap?.equals) {
    if (optionMap.equals[opt]) {
      return optionMap.equals[opt][entity];
    }
  } else if (optionMap?.matches) {
    let regExpKeys = Object.keys(optionMap.matches);
    for (let i = 0; i < regExpKeys.length; i++) {
      let regExp = RegExp(optionMap.matches[regExpKeys[i]]);
      if (regExp.test(opt)) {
        return optionMap.matches[regExpKeys[i]][entity];
      }
    }
  }
};

const findQuestionAnswer = (questions = [], answers = {}, fn) => {
  const foundQuestion = questions.filter(fn);
  if (foundQuestion.length) {
    return answers[foundQuestion[0].id]?.value || false;
  } else {
    return false;
  }
};

const findPageResult = (metadata, screenData, fn) => {
  if (!metadata?.pages?.length) return null;
  let result = null;
  Object.entries(screenData).forEach(([k, v]) => {
    const page = metadata.pages.find((p) => p.id === k);
    if (fn(page)) result = v.value;
  });
  return result;
};

const isLocationPage = (p) => p && ['location_map_selector'].includes(p.component.type);
const isLocationQuestion = (q) => q && q.question.value_type == 'location';

function getPages(type, metadata) {
  if (!metadata?.pages) return [];
  const pageList = metadata.pages.filter((p) => p.sequence.type === type);
  pageList.sort((a, b) => a.sequence.order - b.sequence.order);
  return pageList;
}
